import { CalendarDefinition } from '../calendar/definition.js';
import { CalendarRemap, TimePointParts, TimePointV1 } from '../calendar/time-point.js';
import { CalendarIssue, FoundryImportWarning, ParseErrorCode, WhenKind, WhenPreviewStatus, } from '../calendar/vocabularies.js';
import { ChronEdge, ChronRelation, ClientRecordOrigin, ClockAdvanceKind, ClockReason, ConstraintStatus, EffectiveStatus, PlacementKind, PlacementOrigin, PlacementStatus, Precision, Qualifier, RecordOrigin, TICK_MAX, TICK_MIN, Tick, TickReviewReason, TimeUnit, ViolationKind, } from '../domain/enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, RowVersion, Uuid } from './common.js';
import { JsonObject } from './documents.js';
import { DocRefV1, LinkBoundaryV1, LinkWarningV1, TickSpanV1 } from './links.js';
import { PageQueryV1 } from './list.js';
import { queryBool, queryInt } from './query.js';
/**
 * The chronology shapes of exit 1b (1.0b deliverable 20): the calendar and its edits (1.L-1), the
 * When preview, placements, constraints, the Chronicle, NOW and dated states (1.L-2). Authored
 * time points travel as `TimePointV1`; the engine's results (`TickResultV1`, `SpanResultV1`,
 * `CalendarDiffV1`, `WhenPreviewV1`) are 1.K-1's shapes, so the server returns them unmapped.
 */
const Count = z.number().int().nonnegative();
const Note = z.string().max(10_000);
const Label = z.string().min(1).max(500);
const TickOk = z
    .strictObject({ status: z.literal('ok'), lo: Tick, hi: Tick })
    .refine((r) => r.lo < r.hi, { message: 'lo is below hi', path: ['hi'] });
const TickOpenLo = z.strictObject({ status: z.literal('open_lo'), hi: Tick });
const TickOpenHi = z.strictObject({ status: z.literal('open_hi'), lo: Tick });
/**
 * 1.K-1's `TickResult` (its deliverable 5), one to one with a `time_point`'s derived columns:
 * `hi` exclusive, null on an open side by leaving the key out, `invalid` with its review reason.
 */
export const TickResultV1 = z.discriminatedUnion('status', [
    TickOk,
    TickOpenLo,
    TickOpenHi,
    z.strictObject({ status: z.literal('invalid'), reason: TickReviewReason }),
]);
/** A tick result that resolved: `ok`, `open_lo` or `open_hi`. */
export const ValidTickResultV1 = z.discriminatedUnion('status', [TickOk, TickOpenLo, TickOpenHi]);
/** One span of 1.K-1's `spansOf`: `hi` exclusive, null for unbounded on that side. */
export const SpanBoundsV1 = z.strictObject({ lo: Tick.nullable(), hi: Tick.nullable() });
/** 1.K-1's `SpanResult` (its deliverable 16): an empty window is null; invalid empties both. */
export const SpanResultV1 = z.discriminatedUnion('spanStatus', [
    z.strictObject({
        spanStatus: z.literal('ok'),
        certain: SpanBoundsV1.nullable(),
        possible: SpanBoundsV1.nullable(),
    }),
    z.strictObject({ spanStatus: z.literal('invalid'), certain: z.null(), possible: z.null() }),
]);
/** A stored `time_point`: its id, its authored columns and its derived ticks. */
export const DatedPointV1 = z.strictObject({ id: Uuid, point: TimePointV1, ticks: TickResultV1 });
// ---------------------------------------------------------------------------------- calendar
/** The campaign's one calendar (`calendar`), with its current version's note and source file. */
export const CalendarV1 = z.strictObject({
    id: Uuid,
    name: z.string().min(1).max(200),
    definition: CalendarDefinition,
    version: RowVersion,
    /** `calendar_version.note` and `.source_asset_id` of the current version. */
    note: z.string().max(2000).nullable(),
    sourceAssetId: Uuid.nullable(),
    updatedAt: IsoTimestamp,
});
/**
 * A draft definition is any JSON object: a draft that breaks a rule of `CalendarDefinition` still
 * reaches 1.K-1's `compile`, which reports each problem as a `CalendarIssue` (in the diff's
 * `toErrors`, or in a commit's 422 `calendar_invalid`), never as a 400.
 */
const DraftDefinition = JsonObject;
/** `calendar.diff`: the draft and its remap, checked against every time point in memory. */
export const CalendarDiffRequestV1 = z.strictObject({
    definition: DraftDefinition,
    remap: CalendarRemap,
});
/**
 * `calendar.commit`: a stale `baseVersion` answers 409 `row_conflict`. `baseVersion: 0` means "no
 * calendar row yet": it creates the calendar at version 1 (amendments 2.2 section 2, the first
 * import), and answers 409 once a calendar exists (CS r1d, DECISIONS 98 item 11;
 * `EXAMPLE_CALENDAR_FIRST_COMMIT`).
 */
export const CalendarCommitRequestV1 = z.strictObject({
    baseVersion: z.number().int().min(0),
    definition: DraftDefinition,
    remap: CalendarRemap,
    note: z.string().max(2000).nullable(),
    /** The imported Foundry file (amendments 2.2 section 7, `calendar_version.source_asset_id`). */
    sourceAssetId: Uuid.optional(),
});
/** `details` of 422 `calendar_invalid`: `compile`'s issues, errors among them. */
export const CalendarInvalidDetailsV1 = z.strictObject({ issues: z.array(CalendarIssue).min(1) });
const PointId = Uuid;
const ErrorIssue = z.array(CalendarIssue.options[0]);
const WarningIssue = z.array(CalendarIssue.options[1]);
/**
 * `calendar.diff`'s answer: 1.K-1's `CalendarDiff` (its deliverable 17) field for field, each
 * point in one list, lists sorted by id; the server adds the two version numbers. Episode
 * warnings are null when no lint ran (`warningsOf` not given).
 */
export const CalendarDiffV1 = z.strictObject({
    fromVersion: RowVersion,
    toVersion: RowVersion,
    toErrors: ErrorIssue,
    toWarnings: WarningIssue,
    unchanged: Count,
    moved: z.array(z.strictObject({ id: PointId, before: ValidTickResultV1, after: ValidTickResultV1 })),
    becameInvalid: z.array(z.strictObject({ id: PointId, before: ValidTickResultV1, reason: TickReviewReason })),
    becameValid: z.array(z.strictObject({ id: PointId, after: ValidTickResultV1 })),
    stillInvalid: z.array(z.strictObject({ id: PointId, reasonBefore: TickReviewReason, reasonAfter: TickReviewReason })),
    episodesChanged: z.array(z.strictObject({
        id: Uuid,
        groupKey: z.string().min(1).max(500),
        before: SpanResultV1,
        after: SpanResultV1,
        warningsBefore: z.array(LinkWarningV1).nullable(),
        warningsAfter: z.array(LinkWarningV1).nullable(),
    })),
});
/** `calendar.importFoundry`: a Foundry calendar file's JSON and the season base the GM chose. */
export const CalendarImportRequestV1 = z.strictObject({
    calendar: JsonObject,
    seasonDayBase: z.union([z.literal(0), z.literal(1)]),
});
/** The imported definition (it passed `compile`; nothing is stored) and P0-13's warnings. */
export const CalendarImportV1 = z.strictObject({
    definition: CalendarDefinition,
    warnings: z.array(FoundryImportWarning),
});
// ------------------------------------------------------------------------------ When preview
/** `chrono.preview`: the When input as typed, and the document it is for. */
export const ChronoPreviewRequestV1 = z.strictObject({
    input: z.string().max(500),
    docId: Uuid.optional(),
});
/** A placement's role (`occurs` by default; `born`, `died`, `session` and the GM's own). */
export const PlacementRole = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
/** One of the anchor document's placements, as 1.K-1's `resolveAnchor` reads and offers it. */
export const AnchorOptionV1 = z.strictObject({
    id: Uuid,
    role: PlacementRole,
    effectiveStatus: EffectiveStatus,
    label: Label.nullable(),
    lo: Tick.nullable(),
});
/** 1.K-1's `AnchorChoice`: use the one canon `occurs` placement, create one, or pick. */
export const AnchorChoiceV1 = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('use'), id: Uuid }),
    z.strictObject({ kind: z.literal('create_auto') }),
    z.strictObject({ kind: z.literal('pick'), options: z.array(AnchorOptionV1).min(2) }),
]);
/**
 * `chrono.preview`'s answer: 1.K-1's `WhenPreview` (its deliverable 14) field for field, and the
 * anchor choice the server resolves for a relative phrase whose anchor names a document (null
 * otherwise).
 */
export const WhenPreviewV1 = z.strictObject({
    kind: WhenKind,
    canonical: z.string().nullable(),
    precision: Precision.nullable(),
    qualifier: Qualifier.nullable(),
    status: WhenPreviewStatus,
    reason: z.union([TickReviewReason, ParseErrorCode]).nullable(),
    earliest: Tick.nullable(),
    latest: Tick.nullable(),
    anchorChoice: AnchorChoiceV1.nullable(),
});
// ------------------------------------------------------------------- placements, constraints
/** A document taking part in an event (`placement_participant`); `role` may be empty. */
export const ParticipantV1 = z.strictObject({ docId: Uuid, role: z.string().max(100) });
/** An ordering between two placements' edges (`chron_constraint`); `rv` guards every write. */
export const ConstraintV1 = z.strictObject({
    id: Uuid,
    rv: RowVersion,
    beforeId: Uuid,
    beforeEdge: ChronEdge,
    afterId: Uuid,
    afterEdge: ChronEdge,
    relation: ChronRelation,
    minLagN: z.number().int().nullable(),
    maxLagN: z.number().int().nullable(),
    lagUnit: TimeUnit.nullable(),
    note: Note.nullable(),
    origin: PlacementOrigin,
    status: ConstraintStatus,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/**
 * `constraints.create`: the edges default to `end` before `start` (baseline section 3). Each side
 * names a placement (`beforeId`, `afterId`) or a document (`beforeDocId`, `afterDocId`), exactly one
 * of the two: a document stands for its anchor placement, which the server picks or creates (CS
 * r2b item L1; 1.L-2 deliverable 1). A document side is an owner pointer, so a reference document
 * is refused there. The same document on both sides is the server's to refuse (two sides that
 * resolve to one placement), not the schema's: the reference sweep sends one canon id at every
 * other owner pointer.
 */
export const ConstraintCreateV1 = z
    .strictObject({
    beforeId: Uuid.optional(),
    beforeDocId: Uuid.optional(),
    beforeEdge: ChronEdge.optional(),
    afterId: Uuid.optional(),
    afterDocId: Uuid.optional(),
    afterEdge: ChronEdge.optional(),
    relation: ChronRelation,
    minLagN: z.number().int().nonnegative().optional(),
    maxLagN: z.number().int().nonnegative().optional(),
    lagUnit: TimeUnit.optional(),
    note: Note.optional(),
})
    .refine((c) => (c.beforeId === undefined) !== (c.beforeDocId === undefined), {
    message: 'exactly one of beforeId and beforeDocId',
    path: ['beforeId'],
})
    .refine((c) => (c.afterId === undefined) !== (c.afterDocId === undefined), {
    message: 'exactly one of afterId and afterDocId',
    path: ['afterId'],
})
    .refine((c) => c.beforeId === undefined || c.beforeId !== c.afterId, {
    message: 'two placements',
    path: ['afterId'],
})
    .refine((c) => (c.minLagN === undefined && c.maxLagN === undefined) || c.lagUnit !== undefined, {
    message: 'a lag needs its unit',
    path: ['lagUnit'],
});
/** Baseline section 3's checks on `placement`, for a whole placement or a create. */
function placementRule(p, ctx) {
    const has = (value) => value !== undefined && value !== null;
    const issue = (message, path) => ctx.addIssue({ code: 'custom', message, path: [path] });
    if (p.kind === 'undated' && (has(p.start) || has(p.end)))
        issue('undated has no date', 'start');
    if (p.kind !== undefined && p.kind !== 'undated' && !has(p.start))
        issue('a start', 'start');
    if (p.kind === 'moment' && has(p.end))
        issue('a moment has no end', 'end');
    if (p.ongoing === true && has(p.end))
        issue('ongoing has no end', 'end');
}
/**
 * A placement of a document in the world's time (`placement`), with its time points, its
 * participants and the constraints that name it on either side; `rv` guards every write.
 */
export const PlacementV1 = z
    .strictObject({
    id: Uuid,
    rv: RowVersion,
    documentId: Uuid,
    anchorBlockId: Uuid.nullable(),
    role: PlacementRole,
    fieldDefId: Uuid.nullable(),
    label: Label.nullable(),
    summary: Note.nullable(),
    kind: PlacementKind,
    start: DatedPointV1.nullable(),
    end: DatedPointV1.nullable(),
    ongoing: z.boolean(),
    status: PlacementStatus,
    sessionId: Uuid.nullable(),
    orderKey: z.string().min(1).max(200),
    note: Note.nullable(),
    origin: PlacementOrigin,
    effectiveStatus: EffectiveStatus,
    span: TickSpanV1,
    boundLo: Tick.nullable(),
    boundHi: Tick.nullable(),
    /** Null for an unplaced item (undated and unconstrained). */
    chronRank: z.number().int().nullable(),
    participants: z.array(ParticipantV1),
    constraints: z.array(ConstraintV1),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
})
    .superRefine(placementRule);
/**
 * `placements.list`'s query (CS r2c, item W1; DECISIONS 145 1.O-1 item 2): with `participating`
 * true, the live placements of other documents that name the path document as a participant, in
 * the same order and shape, instead of the path document's own. Read from the query string as
 * `true` or `false`. The real server ignores it until 1.L-2 stage 2, whose spec owns the answer.
 */
export const PlacementsQueryV1 = z.strictObject({ participating: queryBool().optional() });
const PlacementFields = {
    start: TimePointV1.nullable().optional(),
    end: TimePointV1.nullable().optional(),
    ongoing: z.boolean().optional(),
    role: PlacementRole.optional(),
    status: PlacementStatus.optional(),
    participants: z.array(ParticipantV1).max(100).optional(),
    /** The placement it follows in `order_key` (null: first). */
    afterPlacementId: Uuid.nullable().optional(),
};
/**
 * `placements.create` on the document of the path. Dates are authored `TimePointV1`s (the When
 * input parses and materializes them with 1.K-1's engine); a relative phrase is an `undated`
 * placement and a `constraints.create`.
 */
export const PlacementCreateV1 = z
    .strictObject({
    kind: PlacementKind,
    ...PlacementFields,
    label: Label.optional(),
    summary: Note.optional(),
    anchorBlockId: Uuid.optional(),
    fieldDefId: Uuid.optional(),
    sessionId: Uuid.optional(),
    note: Note.optional(),
})
    .superRefine(placementRule);
/** `placements.update`: the row version, then any field; the stored row must stay valid. */
export const PlacementUpdateV1 = z.strictObject({
    baseRv: RowVersion,
    kind: PlacementKind.optional(),
    ...PlacementFields,
    label: Label.nullable().optional(),
    summary: Note.nullable().optional(),
    anchorBlockId: Uuid.nullable().optional(),
    sessionId: Uuid.nullable().optional(),
    note: Note.nullable().optional(),
});
/** `/api/docs/:id/placements/:placementId`. */
export const PlacementParams = z.strictObject({ id: Uuid, placementId: Uuid });
/** `/api/docs/:id/constraints/:constraintId`. */
export const ConstraintParams = z.strictObject({ id: Uuid, constraintId: Uuid });
// -------------------------------------------------------------------------------- Chronicle
/** How the Chronicle groups its rows (baseline section 6, Display). */
export const CHRONICLE_DENSITIES = ['era', 'year', 'month'];
export const ChronicleDensity = z.enum(CHRONICLE_DENSITIES);
/** `chronicle.list`: rows whose span meets `[from, to)`, by `chron_rank`, paged. */
export const ChronicleQueryV1 = z.strictObject({
    ...PageQueryV1.shape,
    from: queryInt(TICK_MIN, TICK_MAX).optional(),
    to: queryInt(TICK_MIN, TICK_MAX).optional(),
    density: ChronicleDensity.optional(),
});
/** A violation the solver records on a placement; `overlay` for a non-canon item's own. */
export const ChronViolationV1 = z.strictObject({
    id: Uuid,
    kind: ViolationKind,
    overlay: z.boolean(),
});
/** A row of the Chronicle or the Unplaced tray: a placement, its document and its violations. */
export const ChronicleRowV1 = z.strictObject({
    placement: PlacementV1,
    document: DocRefV1,
    violations: z.array(ChronViolationV1),
});
// ---------------------------------------------------------------------------------- NOW
/** Baseline section 3's checks on `clock_advance`: a delta has its length, a set its point. */
function advanceRule(a, ctx) {
    const has = (value) => value !== undefined && value !== null;
    const delta = has(a.deltaN) && has(a.deltaUnit) && !has(a.set);
    if ((a.kind === 'delta') !== delta) {
        ctx.addIssue({
            code: 'custom',
            message: 'a delta has deltaN and deltaUnit only',
            path: ['kind'],
        });
    }
    if ((a.kind === 'set') !== has(a.set)) {
        ctx.addIssue({ code: 'custom', message: 'a set has its time point', path: ['set'] });
    }
}
/** One advance of NOW (`clock_advance`); `fromTick` and `toTick` are derived. */
export const ClockAdvanceV1 = z
    .strictObject({
    id: Uuid,
    sessionId: Uuid.nullable(),
    captureId: Uuid.nullable(),
    orderKey: z.string().min(1).max(200),
    kind: ClockAdvanceKind,
    deltaN: z.number().int().nullable(),
    deltaUnit: TimeUnit.nullable(),
    set: DatedPointV1.nullable(),
    reason: ClockReason,
    note: Note.nullable(),
    undoneAt: IsoTimestamp.nullable(),
    origin: RecordOrigin,
    fromTick: Tick.nullable(),
    toTick: Tick.nullable(),
    createdAt: IsoTimestamp,
})
    .superRefine(advanceRule);
/** NOW (`campaign_clock`, one row): its origin, the present, and the latest advances. */
export const ClockV1 = z.strictObject({
    rv: RowVersion,
    origin: DatedPointV1,
    presentTick: Tick.nullable(),
    presentParts: TimePointParts.nullable(),
    /** The latest advances, undone ones included, newest first. */
    advances: z.array(ClockAdvanceV1).max(50),
});
/**
 * `clock.advance`: a delta or a set, against the clock's row version. `baseRv: 0` means "no clock
 * yet": a `set` then creates the clock at rv 1 with the set point as its origin and no advance, and
 * answers 409 `row_conflict` once a clock exists (CS r2b item L3, as `CalendarCommitRequestV1`'s
 * `baseVersion: 0`; `EXAMPLE_CLOCK_FIRST_SET`).
 */
export const ClockAdvanceRequestV1 = z
    .strictObject({
    kind: ClockAdvanceKind,
    deltaN: z.number().int().optional(),
    deltaUnit: TimeUnit.optional(),
    set: TimePointV1.optional(),
    reason: ClockReason,
    sessionId: Uuid.optional(),
    baseRv: z.number().int().min(0),
    /**
     * CS r5a deliverable 143: the session capture the advance comes from (2.C s3's promote writes
     * it; Run mode sends none, DECISIONS 219 ruling 4), and the origin a request may send.
     */
    captureId: Uuid.optional(),
    origin: ClientRecordOrigin.optional(),
})
    .superRefine(advanceRule);
/** `clock.undo` and every write that names only the row version it was based on. */
export const BaseRvRequestV1 = z.strictObject({ baseRv: RowVersion });
// --------------------------------------------------------------------------------- states
/**
 * A dated value of a temporal scalar field (`entity_state`): a value from `from` until `to`, each
 * a time point, a placement edge, or null for unbounded; `rv` guards every write.
 */
export const EntityStateV1 = z.strictObject({
    id: Uuid,
    rv: RowVersion,
    documentId: Uuid,
    fieldDefId: Uuid,
    value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
    from: LinkBoundaryV1.nullable(),
    to: LinkBoundaryV1.nullable(),
    sessionId: Uuid.nullable(),
    note: Note.nullable(),
    origin: PlacementOrigin,
    certainSpan: TickSpanV1,
    possibleSpan: TickSpanV1,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/** `states.create` on the document of the path. */
export const StateCreateV1 = z.strictObject({
    fieldDefId: Uuid,
    value: EntityStateV1.shape.value,
    from: LinkBoundaryV1.nullable().optional(),
    to: LinkBoundaryV1.nullable().optional(),
    sessionId: Uuid.optional(),
    note: Note.optional(),
});
/** `states.update`: the row version, then any of the value, its bounds, its session and note. */
export const StateUpdateV1 = z.strictObject({
    baseRv: RowVersion,
    value: EntityStateV1.shape.value.optional(),
    from: LinkBoundaryV1.nullable().optional(),
    to: LinkBoundaryV1.nullable().optional(),
    sessionId: Uuid.nullable().optional(),
    note: Note.nullable().optional(),
});
/** `/api/docs/:id/states/:stateId`. */
export const StateParams = z.strictObject({ id: Uuid, stateId: Uuid });
//# sourceMappingURL=chronology.js.map